/**
 * SearchInput - Reusable search input with clear button
 * 
 * Features:
 * - Search icon on left
 * - Clear (X) button when there's text
 * - Loading spinner support
 * - Consistent styling across the app
 */

import { forwardRef } from 'react';
import { Input, Button } from '@/components/ui';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  className?: string;
  autoFocus?: boolean;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(({
  value,
  onChange,
  placeholder = 'Search...',
  isLoading = false,
  className = '',
  autoFocus = false,
}, ref) => {
  const handleClear = () => {
    onChange('');
  };

  return (
    <div className={`relative ${className}`}>
      {/* Search icon */}
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
        🔍
      </span>
      
      {/* Input */}
      <Input
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="w-full pl-10 pr-10"
      />
      
      {/* Right side: loading spinner or clear button */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
        {isLoading && (
          <span className="text-muted-foreground animate-spin">⏳</span>
        )}
        {value && !isLoading && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={handleClear}
            className="p-0.5 text-muted-foreground hover:text-foreground rounded-full"
            title="Clear search"
          >
            <svg 
              className="w-4 h-4" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </Button>
        )}
      </div>
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;

